import {MAX_LIFETIME, selectedJobs, connectionSummary, proposalResponse, approvalResponse, receiptResponse, progressResponse, storedReceipt, validId, requireValue} from './cohortContract.js'
const journalPrefix = 'sr_native_cohort_receipt_v1:'
const completedPrefix = 'sr_native_cohort_completed_v1:'
const receiptIdentity = value => JSON.stringify([value.version, value.actorId, value.parentGrantId, value.challengeId, value.digest, value.cohortId, value.total, value.expiresAt])
const sameReceipt = (left, right) => !!left && !!right && receiptIdentity(left) === receiptIdentity(right)
export function browserCohortJournal(storage = () => globalThis.localStorage) {
  return {
    read(actorId) {
      const raw = storage().getItem(journalPrefix + actorId)
      if (raw === null) return null
      requireValue(raw.length < 4000)
      const value = storedReceipt(JSON.parse(raw), actorId), identity = receiptIdentity(value)
      const completed = storage().getItem(completedPrefix + identity)
      if (completed !== null) { requireValue(completed === identity); return null }
      return value
    },
    write(receipt) {
      const value = storedReceipt(receipt, receipt.actorId), key = journalPrefix + receipt.actorId
      storage().setItem(key, JSON.stringify(value))
      requireValue(storage().getItem(key) === JSON.stringify(value))
    },
    retireCompleted(receipt) {
      const value = storedReceipt(receipt, receipt.actorId), identity = receiptIdentity(value), key = journalPrefix + value.actorId
      requireValue(validId(value.cohortId))
      const raw = storage().getItem(key)
      requireValue(raw !== null && raw.length < 4000 && sameReceipt(storedReceipt(JSON.parse(raw), value.actorId), value))
      // Never remove/overwrite the active pointer: localStorage has no atomic
      // compare-and-delete across tabs. This exact completed marker preserves
      // the old receipt and cannot hide a concurrently written different one.
      const completedKey = completedPrefix + identity
      storage().setItem(completedKey, identity)
      requireValue(storage().getItem(completedKey) === identity && storage().getItem(key) === raw)
    },
  }
}
export function cohortScope(context) {
  return JSON.stringify([context.actorId, context.session, context.jobs])
}
const freshState = () => ({phase: 'ready', busy: false, connections: null, parentGrantId: '', hours: 24, proposal: null, acknowledged: false, receipt: null, progress: null, revokeConfirm: false, error: ''})
// Every write is an explicit handler. Construction, reopening and reading never
// propose, consent, revoke, admit a request or invoke a generation tool.
export function createCohortController({api: transport, getContext, journal, onState, now = Date.now, timeoutMs = 15000}) {
  const context = getContext(), scope = cohortScope(context)
  let state = freshState(), live = true, lock = false, pending = null, sequence = 0
  const current = () => live && cohortScope(getContext()) === scope && validId(context.actorId)
  const publish = patch => { state = {...state, ...patch}; if (current()) onState({...state}) }
  let selection
  try { selection = selectedJobs(context.jobs) } catch { selection = null }
  try {
    if (!validId(context.actorId)) throw Error('owner_required')
    const receipt = journal.read(context.actorId)
    if (receipt) state = {...state, phase: 'recovery', receipt}
  } catch { state = {...state, phase: 'blocked', error: 'Current owner identity or recovery storage is unavailable. No approval can be submitted.'} }
  const clearProposal = {proposal: null, acknowledged: false}
  function close() {
    live = false; sequence++; pending?.abort(); pending = null
  }
  async function run(phase, operation, success, failure) {
    if (!current() || lock) return
    lock = true
    const epoch = ++sequence, abort = new AbortController()
    pending = abort; publish({busy: true, phase, error: ''})
    let timeout
    const timedOut = new Promise((_, reject) => {
      abort.signal.addEventListener('abort', () => reject(Error('cohort_aborted')), {once: true})
      timeout = setTimeout(() => { abort.abort(); reject(Error('cohort_timeout')) }, timeoutMs)
    })
    try {
      const result = await Promise.race([Promise.resolve().then(() => { requireValue(current() && !abort.signal.aborted); return operation(abort.signal) }), timedOut])
      if (current() && epoch === sequence && !abort.signal.aborted) success(result)
    } catch (error) {
      if (current() && epoch === sequence) failure(error)
    } finally {
      clearTimeout(timeout)
      if (pending === abort) pending = null
      if (epoch === sequence) { lock = false; publish({busy: false}) }
    }
  }
  return Object.freeze({
    scope,
    snapshot: () => ({...state}),
    close,
    readConnections() {
      if (!current() || state.receipt || ['blocked', 'retired'].includes(state.phase) || lock) return
      publish({...clearProposal, parentGrantId: '', connections: null})
      return run('loading_connections', signal => transport.connections({signal}), value => {
        const connections = connectionSummary(value, context.actorId, now())
        publish({phase: 'ready', connections})
      }, () => publish({phase: 'ready', connections: null, error: 'Current native connections could not be verified. Review is unavailable; no approval was submitted.'}))
    },
    selectParent(parentGrantId) {
      if (!current() || lock || state.receipt || state.phase === 'retired' || !state.connections?.some(row => row.parentGrantId === parentGrantId && row.expiresAt > now())) return
      publish({phase: 'ready', parentGrantId, ...clearProposal, error: ''})
    },
    selectHours(hours) {
      if (!current() || lock || state.receipt || state.phase === 'retired' || ![1, 6, 24].includes(hours)) return
      publish({phase: 'ready', hours, ...clearProposal, error: ''})
    },
    acknowledge(value) {
      if (!current() || lock || state.phase !== 'review' || !state.proposal) return
      publish({acknowledged: value === true})
    },
    propose() {
      if (!current() || lock || state.receipt || !selection || !['ready', 'review'].includes(state.phase)) return
      const parent = state.connections?.find(row => row.parentGrantId === state.parentGrantId)
      if (!parent || parent.expiresAt <= now()) return
      const expiresAt = Math.min(now() + Math.min(state.hours * 3600000, MAX_LIFETIME), parent.expiresAt)
      // Another tab's uncertain/accepted approval must be reconciled, not replaced.
      try {
        const existing = journal.read(context.actorId)
        if (existing) { publish({phase: 'recovery', receipt: existing, ...clearProposal}); return }
      } catch { publish({phase: 'blocked', error: 'Recovery storage could not be verified. Review is blocked.'}); return }
      publish(clearProposal)
      return run('proposing', signal => transport.propose({parentGrantId: parent.parentGrantId, jobIds: selection.map(row => row.jobId), expiresAt}, {signal}), value => {
        const proposal = proposalResponse(value, selection, expiresAt)
        requireValue(expiresAt > now())
        publish({phase: 'review', proposal, reviewDeadline: Math.min(expiresAt, now() + 540000)})
      }, () => publish({phase: 'ready', ...clearProposal, error: 'The exact selected jobs could not be reviewed. No approval was submitted. Refresh current artwork or connection access before another review.'}))
    },
    approve() {
      if (!current() || lock || state.phase !== 'review' || !state.acknowledged || !state.proposal || state.receipt) return
      const proposal = state.proposal, parent = state.connections?.find(row => row.parentGrantId === state.parentGrantId)
      if (!parent || proposal.expiresAt <= now() || state.reviewDeadline <= now() || parent.expiresAt < proposal.expiresAt) { publish({phase: 'ready', ...clearProposal, error: 'This review has expired. Read the current connection and review again.'}); return }
      const receipt = {version: 1, actorId: context.actorId, parentGrantId: parent.parentGrantId, challengeId: proposal.challengeId, digest: proposal.digest, cohortId: null, total: proposal.total, expiresAt: proposal.expiresAt}
      try {
        const existing = journal.read(context.actorId)
        if (existing) { publish({phase: 'recovery', receipt: existing, ...clearProposal}); return }
        journal.write(receipt)
      } catch { publish({phase: 'blocked', ...clearProposal, error: 'The recovery record could not be saved. Approval was not submitted.'}); return }
      publish({receipt, acknowledged: false})
      return run('approving', signal => transport.consent({challengeId: proposal.challengeId, expectedDigest: proposal.digest}, {signal}), value => {
        const approved = approvalResponse(value, proposal), saved = {...receipt, cohortId: approved.cohortId}
        journal.write(saved)
        publish({phase: 'recorded', receipt: saved, ...clearProposal})
      }, () => publish({phase: 'recovery', ...clearProposal, error: 'The approval outcome is unconfirmed. Check this exact consent receipt. Do not submit another approval.'}))
    },
    readReceipt() {
      if (!current() || lock || !state.receipt || state.phase === 'blocked') return
      const receipt = state.receipt
      return run('reading_receipt', signal => transport.receipt(receipt.challengeId, {signal}), value => {
        const result = receiptResponse(value, receipt)
        if (result.state === 'pending') { publish({phase: 'recovery', error: 'No cohort was recorded at the time of this read. The earlier approval may still be unresolved. No replacement or repeated approval will be sent.'}); return }
        const saved = {...receipt, cohortId: result.cohortId}; journal.write(saved)
        publish({phase: 'recorded', receipt: saved, ...clearProposal})
      }, () => publish({phase: 'recovery', error: 'The exact consent receipt is unavailable. Its outcome remains unknown; no new approval will be sent.'}))
    },
    readProgress() {
      if (!current() || lock || !state.receipt?.cohortId || state.phase === 'blocked') return
      const receipt = state.receipt
      return run('reading_progress', signal => transport.progress(receipt.cohortId, {signal}), value => {
        const progress = progressResponse(value, receipt)
        publish({phase: progress.state === 'completed' ? 'completed' : 'recorded', progress, revokeConfirm: false})
      }, () => publish({phase: 'recorded', progress: null, revokeConfirm: false, error: 'Current progress could not be verified. Completion counts are unknown, not zero. Do not start a replacement cohort.'}))
    },
    retireCompleted() {
      if (!current() || lock || state.phase !== 'completed' || state.progress?.state !== 'completed' || !state.receipt?.cohortId) return
      const receipt = state.receipt
      return run('retiring_completed', signal => transport.progress(receipt.cohortId, {signal}), value => {
        const progress = progressResponse(value, receipt)
        requireValue(progress.state === 'completed' && progress.currentBatch.endPositionExclusive === receipt.total)
        requireValue(current() && sameReceipt(journal.read(context.actorId), receipt))
        requireValue(current())
        journal.retireCompleted(receipt)
        requireValue(current() && journal.read(context.actorId) === null)
        publish({...freshState(), phase: 'retired'})
      }, () => {
        let receipt = state.receipt
        try { receipt = journal.read(context.actorId) || receipt } catch { /* Preserve the existing recovery pointer. */ }
        publish({phase: 'recovery', receipt, progress: null, revokeConfirm: false, error: 'Completion or its exact recovery record could not be verified. Existing receipts remain preserved; read current progress before another selection.'})
      })
    },
    confirmRevoke(value) {
      if (!current() || lock || !state.receipt?.cohortId || state.phase === 'blocked' || state.phase === 'completed') return
      publish({revokeConfirm: value === true})
    },
    revoke() {
      if (!current() || lock || !state.revokeConfirm || !state.receipt?.cohortId || state.phase === 'blocked' || state.phase === 'completed') return
      return run('revoking', signal => transport.revoke(state.receipt.cohortId, {signal}), value => {
        requireValue(value?.ok === true && (value.state === 'completed' || value.state === 'held' && value.reason === 'cohort_revoked'))
        publish({phase: value.state === 'completed' ? 'completed' : 'revoked', progress: null, revokeConfirm: false})
      }, () => publish({phase: 'recorded', progress: null, revokeConfirm: false, error: 'Revocation is unconfirmed. Read this cohort’s current progress before any further action.'}))
    },
  })
}
